import { useEffect, useState } from "react";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PsychologyIcon from "@mui/icons-material/Psychology";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { listBurnoutRisk } from "@/api/burnout";
import { explainGaps } from "@/api/planning";
import { listOrganizations } from "@/api/organizations";
import { getSkillStats } from "@/api/skills";
import { listAllUnavailabilities } from "@/api/unavailabilities";
import { PageHeader } from "@/components/PageHeader";
import { useAuthStore } from "@/store/authStore";

type Severity = "success" | "info" | "warning" | "error";

function HealthCard({
  icon,
  title,
  value,
  hint,
  to,
  severity,
}: {
  icon: React.ReactNode;
  title: string;
  value: number | string;
  hint: string;
  to: string;
  severity: Severity;
}) {
  return (
    <Card
      variant="outlined"
      component={RouterLink}
      to={to}
      sx={{
        textDecoration: "none",
        color: "inherit",
        height: "100%",
        borderLeft: 4,
        borderLeftColor: `${severity}.main`,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ color: `${severity}.main` }}>{icon}</Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      </CardContent>
    </Card>
  );
}

export function OrgHealthPage() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
  });

  const [orgId, setOrgId] = useState<string>("");
  useEffect(() => {
    const first = orgs.data?.items[0];
    if (!orgId && first) {
      setOrgId(
        me?.active_org_id && orgs.data?.items.find((o) => o.id === me.active_org_id)
          ? me.active_org_id
          : first.id,
      );
    }
  }, [orgs.data, orgId, me?.active_org_id]);

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;

  const burnout = useQuery({
    queryKey: ["burnout-org", orgId],
    queryFn: () => listBurnoutRisk({ organization_id: orgId, days: 30 }),
    enabled: Boolean(orgId),
  });

  const pendingUnavail = useQuery({
    queryKey: ["pending-unavail"],
    queryFn: () => listAllUnavailabilities({ status: "PENDING" }),
    enabled: Boolean(orgId),
  });

  const gaps = useQuery({
    queryKey: ["explain-gaps", orgId, year, month],
    queryFn: () => explainGaps(orgId, year, month),
    enabled: Boolean(orgId),
  });

  const skillStats = useQuery({
    queryKey: ["skill-stats", orgId],
    queryFn: () => getSkillStats(orgId),
    enabled: Boolean(orgId),
  });

  if (orgs.isLoading) return <CircularProgress />;
  if (!orgId) {
    return (
      <Box>
        <PageHeader title={t("orgHealth.title")} subtitle={t("orgHealth.subtitle")} helpTerm="santeOrg" />
        <Alert severity="info">{t("orgHealth.pickOrg")}</Alert>
      </Box>
    );
  }

  const burnoutHigh = (burnout.data ?? []).filter(
    (r) => r.risk_level === "HIGH" || r.risk_level === "CRITICAL",
  ).length;
  const pendingCount = pendingUnavail.data?.length ?? 0;
  const gapCells = (gaps.data?.cells ?? []).filter((c) => c.free_candidates === 0).length;
  const totalCells = gaps.data?.cells.length ?? 0;
  const skillRisks = (skillStats.data?.items ?? []).filter(
    (s) => s.slot_templates_requiring > 0 && s.coverage_ratio < 0.15,
  );

  return (
    <Box>
      <PageHeader
        title={t("orgHealth.title")}
        subtitle={t("orgHealth.subtitle")}
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>{t("nav.organizations")}</InputLabel>
          <Select
            label={t("nav.organizations")}
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} — {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <HealthCard
            icon={<MonitorHeartIcon />}
            title={t("orgHealth.burnoutTitle")}
            value={burnoutHigh}
            hint={t("orgHealth.burnoutHint")}
            to="/burnout"
            severity={
              burnoutHigh >= 3 ? "error" : burnoutHigh >= 1 ? "warning" : "success"
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <HealthCard
            icon={<PendingActionsIcon />}
            title={t("orgHealth.pendingUnavailTitle")}
            value={pendingCount}
            hint={t("orgHealth.pendingUnavailHint")}
            to="/admin/unavailabilities"
            severity={
              pendingCount >= 5 ? "warning" : pendingCount >= 1 ? "info" : "success"
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <HealthCard
            icon={<HelpOutlineIcon />}
            title={t("orgHealth.gapsTitle")}
            value={`${gapCells} / ${totalCells}`}
            hint={t("orgHealth.gapsHint", {
              month: month.toString().padStart(2, "0"),
              year,
            })}
            to="/planning"
            severity={
              totalCells === 0
                ? "info"
                : gapCells / Math.max(1, totalCells) >= 0.2
                ? "error"
                : gapCells > 0
                ? "warning"
                : "success"
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <HealthCard
            icon={<PsychologyIcon />}
            title={t("orgHealth.skillRiskTitle")}
            value={skillRisks.length}
            hint={t("orgHealth.skillRiskHint")}
            to="/admin/skills"
            severity={
              skillRisks.length >= 2 ? "error" : skillRisks.length === 1 ? "warning" : "success"
            }
          />
        </Grid>
      </Grid>

      {skillRisks.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {t("orgHealth.skillRiskDetailTitle")}
            </Typography>
            <Stack spacing={1}>
              {skillRisks.map((s) => (
                <Stack key={s.skill_id} direction="row" alignItems="center" spacing={1}>
                  <Chip
                    size="small"
                    label={s.code}
                    sx={{
                      bgcolor: s.color ?? undefined,
                      color: s.color ? "#fff" : undefined,
                      fontFamily: "monospace",
                    }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {s.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.practitioner_count} / {s.org_active_total} ·{" "}
                    {Math.round(s.coverage_ratio * 100)}% · {t("orgHealth.requiredOn", {
                      count: s.slot_templates_requiring,
                    })}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {burnout.data && burnoutHigh > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {t("orgHealth.topAtRiskTitle")}
            </Typography>
            <Stack spacing={1}>
              {burnout.data
                .filter((r) => r.risk_level === "HIGH" || r.risk_level === "CRITICAL")
                .slice(0, 5)
                .map((r) => (
                  <Stack key={r.practitioner_id} direction="row" alignItems="center" spacing={1}>
                    <Chip
                      size="small"
                      color={r.risk_level === "CRITICAL" ? "error" : "warning"}
                      label={r.risk_level}
                    />
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                      {r.last_name}
                      {r.first_initial ? `. ${r.first_initial}` : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.score}/100 · {r.weighted_hours.toFixed(0)}h pondérées ·{" "}
                      {r.night_shifts > 0 ? `${r.night_shifts} nuits · ` : ""}
                      {r.max_consecutive_days} j. consécutifs
                    </Typography>
                  </Stack>
                ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
